/**
 * Safe clipboard access for Web and Android.
 * On Android 10+, clipboard is denied when the app is not in focus.
 * This helper only attempts write when the document is visible and swallows errors
 * so the system "Denying clipboard access" log doesn't indicate a broken app.
 */

/**
 * Write text to clipboard. Safe to call from user actions (e.g. button click).
 * On Android, call only when app has focus (e.g. in response to tap); avoids
 * "Denying clipboard access to com.gograbjob.app, application is not in focus".
 *
 * @returns true if write succeeded, false otherwise (e.g. not in focus, permission denied)
 */
export async function safeClipboardWrite(text: string): Promise<boolean> {
  if (!text || typeof navigator?.clipboard?.writeText !== 'function') {
    return false;
  }
  // Only attempt when the app page is visible (reduces Android "not in focus" denials)
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely read text from clipboard (e.g. in paste handler).
 * On Android, reading when app is not in focus is denied; use in user-triggered paste only.
 * @returns clipboard text or empty string if unavailable/denied
 */
export function safeClipboardRead(e: { clipboardData: DataTransfer | null }): string {
  try {
    return e.clipboardData?.getData('text/plain') ?? e.clipboardData?.getData('text') ?? '';
  } catch {
    return '';
  }
}
